"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Box, Grid, Paper, Typography, Button, Divider, CircularProgress } from "@mui/material"
import { ArrowBack } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import StatusBadge from "../../components/common/StatusBadge"
import { getSubscription } from "../../services/subscription.service"

const SubscriptionDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user, selectedOrganization } = useAuth()
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)

  const organizationId = selectedOrganization?._id || user?.organizationId

  useEffect(() => {
    if (organizationId && id) {
      fetchSubscription()
    }
  }, [organizationId, id])

  const fetchSubscription = async () => {
    setLoading(true)
    try {
      const response = await getSubscription(organizationId)
      if (response.data && response.data._id === id) {
        setSubscription(response.data)
      } else {
        showNotification("Subscription not found", "error")
        navigate("/subscriptions")
      }
    } catch (error) {
      showNotification("Failed to fetch subscription details", "error")
      navigate("/subscriptions")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!subscription) {
    return null // Or display an error message
  }

  return (
    <Box>
      <PageHeader
        title="Subscription Details"
        subtitle={`Details for subscription to ${subscription.planId?.name}`}
        breadcrumbs={[
          { text: "Dashboard", link: "/dashboard" },
          { text: "Subscriptions", link: "/subscriptions" },
          { text: "Details" },
        ]}
      />

      <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate("/subscriptions")} sx={{ mb: 3 }}>
        Back to Subscriptions
      </Button>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Subscription Information</Typography>
          <StatusBadge status={subscription.status} />
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Plan
            </Typography>
            <Typography variant="body1">{subscription.planId?.name}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Billing Cycle
            </Typography>
            <Typography variant="body1">{subscription.billingCycle}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Start Date
            </Typography>
            <Typography variant="body1">{new Date(subscription.startDate).toLocaleDateString()}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Current Period End
            </Typography>
            <Typography variant="body1">{new Date(subscription.currentPeriodEnd).toLocaleDateString()}</Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

export default SubscriptionDetails

